// src/utils/signingUtils.ts
// Utilities for legally defensible e-signatures

import { SigningAuditEvent, SigningEventType } from '../types/contract.types';
import { jsPDF } from 'jspdf';
import { PDFDocument } from 'pdf-lib';
import { format } from 'date-fns';

// Get the user's IP address using a public API
export async function getClientIP(): Promise<string> {
  try {
    // Using ipify API - free and reliable
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip || 'unknown';
  } catch (error) {
    console.error('Error fetching IP address:', error);
    // Fallback - in production you might want to use a server-side solution
    return 'unknown';
  }
}

// Get the user agent string
export function getUserAgent(): string {
  return navigator.userAgent || 'unknown';
}

// Calculate SHA-256 hash of a document (from URL)
// Note: May fail due to CORS restrictions on Firebase Storage URLs from localhost
export async function hashDocument(documentUrl: string): Promise<string> {
  try {
    // Fetch the document
    const response = await fetch(documentUrl, { mode: 'cors' });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();

    // Calculate SHA-256 hash
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);

    // Convert to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  } catch (error) {
    console.error('Error hashing document:', error);
    // Return a timestamp-based identifier if we can't hash (CORS issues in dev)
    // In production with proper CORS config, this should work
    const fallbackHash = `unable-to-hash-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    console.warn('Using fallback hash due to CORS/fetch error:', fallbackHash);
    return fallbackHash;
  }
}

// Create an audit event
export function createAuditEvent(
  eventType: SigningEventType,
  userId: string,
  userName: string,
  userEmail: string,
  ipAddress: string,
  userAgent: string,
  metadata?: Record<string, any>
): SigningAuditEvent {
  return {
    eventType,
    timestamp: new Date().toISOString(),
    userId,
    userName,
    userEmail,
    ipAddress,
    userAgent,
    metadata
  };
}

// Generate a signing certificate PDF
export async function generateSigningCertificate(
  contractTitle: string,
  contractType: string,
  documentHash: string,
  signerName: string,
  signerEmail: string,
  signatureType: 'drawn' | 'typed',
  signedAt: Date,
  ipAddress: string,
  userAgent: string,
  auditTrail: SigningAuditEvent[],
  signatureImageData?: string
): Promise<Blob> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let yPos = 20;

  // Helper to ensure string values (jsPDF throws if value is undefined/null)
  const safeStr = (val: any, fallback: string = 'N/A'): string => {
    if (val === undefined || val === null || val === '') return fallback;
    return String(val);
  };

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Certificate of Electronic Signature', pageWidth / 2, yPos, { align: 'center' });
  yPos += 15;

  // Subtitle
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('This certificate confirms the electronic signature was applied in accordance with', pageWidth / 2, yPos, { align: 'center' });
  yPos += 6;
  doc.text('the E-SIGN Act and UETA', pageWidth / 2, yPos, { align: 'center' });
  yPos += 15;

  // Divider
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 15;

  // Document Information Section
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Document Information', margin, yPos);
  yPos += 10;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const addRow = (label: string, value: string) => {
    doc.setFont('helvetica', 'bold');
    doc.text(safeStr(label) + ':', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(safeStr(value), margin + 45, yPos);
    yPos += 7;
  };

  addRow('Document', safeStr(contractTitle, 'Untitled Contract'));
  addRow('Type', safeStr(contractType, 'General'));
  addRow('Document Hash', safeStr(documentHash, 'N/A').substring(0, 32) + '...');
  yPos += 5;

  // Signer Information Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Signer Information', margin, yPos);
  yPos += 10;

  doc.setFontSize(10);
  addRow('Name', safeStr(signerName, 'Unknown'));
  addRow('Email', safeStr(signerEmail, 'Unknown'));
  addRow('Signature Type', signatureType === 'drawn' ? 'Hand-drawn' : 'Typed');
  addRow('Signed At', format(signedAt || new Date(), "MMMM d, yyyy 'at' h:mm:ss a"));
  addRow('IP Address', safeStr(ipAddress, 'Unknown'));
  yPos += 5;

  // Signature Image (if available)
  if (signatureImageData) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Signature', margin, yPos);
    yPos += 5;

    try {
      doc.addImage(signatureImageData, 'PNG', margin, yPos, 60, 20);
      yPos += 25;
    } catch (e) {
      console.error('Error adding signature image:', e);
    }
  }

  // Audit Trail Section
  yPos += 5;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Audit Trail', margin, yPos);
  yPos += 10;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  // Table header
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, yPos - 4, pageWidth - margin * 2, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('Event', margin + 2, yPos);
  doc.text('Timestamp', margin + 60, yPos);
  doc.text('IP Address', margin + 120, yPos);
  yPos += 8;

  doc.setFont('helvetica', 'normal');
  for (const event of auditTrail || []) {
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }

    const eventName = formatEventType(event.eventType);
    const eventTime = event.timestamp ? format(new Date(event.timestamp), 'MM/dd/yyyy HH:mm:ss') : 'N/A';

    doc.text(safeStr(eventName), margin + 2, yPos);
    doc.text(safeStr(eventTime), margin + 60, yPos);
    doc.text(safeStr(event.ipAddress, 'Unknown'), margin + 120, yPos);
    yPos += 6;
  }

  // Footer
  yPos = doc.internal.pageSize.getHeight() - 30;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text('This certificate is generated automatically and serves as a legal record of the electronic signing ceremony.', pageWidth / 2, yPos, { align: 'center' });
  yPos += 5;
  doc.text('The document hash uniquely identifies the exact version of the document that was signed.', pageWidth / 2, yPos, { align: 'center' });
  yPos += 5;
  doc.text(`Generated: ${format(new Date(), "MMMM d, yyyy 'at' h:mm:ss a")}`, pageWidth / 2, yPos, { align: 'center' });

  // Full document hash on last page
  doc.addPage();
  yPos = 20;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Technical Details', margin, yPos);
  yPos += 15;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Full Document Hash (SHA-256):', margin, yPos);
  yPos += 8;

  doc.setFontSize(8);
  doc.setFont('courier', 'normal');
  // Split hash into multiple lines for readability
  const safeHash = safeStr(documentHash, 'N/A');
  const hashLine1 = safeHash.substring(0, 32) || 'N/A';
  const hashLine2 = safeHash.length > 32 ? safeHash.substring(32, 64) : '';
  doc.text(hashLine1, margin, yPos);
  yPos += 5;
  if (hashLine2) {
    doc.text(hashLine2, margin, yPos);
    yPos += 15;
  } else {
    yPos += 10;
  }

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('User Agent:', margin, yPos);
  yPos += 8;

  doc.setFontSize(8);
  // Split user agent into multiple lines if needed
  const maxWidth = pageWidth - margin * 2;
  const safeUserAgent = safeStr(userAgent, 'Unknown');
  const userAgentLines = doc.splitTextToSize(safeUserAgent, maxWidth);
  doc.text(userAgentLines, margin, yPos);

  return doc.output('blob');
}

// Format event type for display
function formatEventType(eventType: SigningEventType): string {
  const labels: Record<SigningEventType, string> = {
    'document_opened': 'Document Opened',
    'document_viewed': 'Document Viewed',
    'document_reviewed': 'Document Reviewed',
    'acknowledgment_accepted': 'Acknowledgment Accepted',
    'esign_consent_given': 'E-Sign Consent Given',
    'signature_applied': 'Signature Applied',
    'signing_completed': 'Signing Completed',
    'certificate_generated': 'Certificate Generated'
  };
  return labels[eventType] || eventType;
}

// Helper to ensure string values
function safeStr(val: any, fallback: string = 'N/A'): string {
  if (val === undefined || val === null || val === '') return fallback;
  return String(val);
}

// Contract type for package generation
interface ContractForPackage {
  id: string;
  title: string;
  description?: string;
  documentUrl: string;
  documentName: string;
  documentHash?: string;
  contractType?: string;
  status: string;
  createdAt: any;
  completedAt?: any;
  createdByName: string;
  signatures: {
    signedBy: string;
    signedByName: string;
    signedByEmail: string;
    signedAt: any;
    signatureData: string;
    signatureType: 'drawn' | 'typed' | 'uploaded';
    ipAddress: string;
    userAgent: string;
    documentHash: string;
    geolocation?: { latitude: number; longitude: number; accuracy: number };
    auditTrail: SigningAuditEvent[];
  }[];
  requiredSigners: {
    userId: string;
    userName: string;
    email: string;
    role: string;
    signed: boolean;
  }[];
}

// Generate a complete signing package PDF with all signatures and audit data
export async function generateCompleteSigningPackage(
  contract: ContractForPackage
): Promise<Blob> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = 20;

  // Helper to check and add new page if needed
  const checkNewPage = (requiredSpace: number = 30) => {
    if (yPos > pageHeight - requiredSpace) {
      doc.addPage();
      yPos = 20;
    }
  };

  // Helper to add a row
  const addRow = (label: string, value: string, labelWidth: number = 45) => {
    checkNewPage();
    doc.setFont('helvetica', 'bold');
    doc.text(safeStr(label) + ':', margin, yPos);
    doc.setFont('helvetica', 'normal');
    const valueLines = doc.splitTextToSize(safeStr(value), pageWidth - margin * 2 - labelWidth);
    doc.text(valueLines, margin + labelWidth, yPos);
    yPos += 7 * Math.max(1, valueLines.length);
  };

  // =========================================
  // PAGE 1: Cover Page / Contract Summary
  // =========================================

  // Header with logo-like styling
  doc.setFillColor(99, 102, 241); // Primary purple
  doc.rect(0, 0, pageWidth, 50, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Electronic Signature Certificate', pageWidth / 2, 25, { align: 'center' });

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Complete Signing Package', pageWidth / 2, 38, { align: 'center' });

  yPos = 70;
  doc.setTextColor(0, 0, 0);

  // Contract Information Box
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, yPos - 5, pageWidth - margin * 2, 60, 3, 3, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, yPos - 5, pageWidth - margin * 2, 60, 3, 3, 'S');

  yPos += 5;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(99, 102, 241);
  doc.text('Contract Information', margin + 5, yPos);
  yPos += 12;

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  addRow('Document Title', safeStr(contract.title, 'Untitled Contract'));
  addRow('Document Name', safeStr(contract.documentName));
  addRow('Contract Type', safeStr(contract.contractType, 'General').replace('_', ' ').toUpperCase());
  addRow('Contract ID', contract.id);

  yPos += 15;

  // Status Box
  const statusColor = contract.status === 'completed'
    ? { bg: [220, 252, 231], text: [22, 163, 74] }  // Green
    : { bg: [254, 243, 199], text: [202, 138, 4] }; // Yellow

  doc.setFillColor(statusColor.bg[0], statusColor.bg[1], statusColor.bg[2]);
  doc.roundedRect(margin, yPos, pageWidth - margin * 2, 25, 3, 3, 'F');

  yPos += 10;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(statusColor.text[0], statusColor.text[1], statusColor.text[2]);
  doc.text(`Status: ${contract.status.toUpperCase().replace('_', ' ')}`, margin + 10, yPos);

  if (contract.completedAt) {
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const completedDate = contract.completedAt.toDate ? contract.completedAt.toDate() : new Date(contract.completedAt);
    doc.text(`Completed: ${format(completedDate, "MMMM d, yyyy 'at' h:mm:ss a")}`, margin + 10, yPos);
  }

  yPos += 25;
  doc.setTextColor(0, 0, 0);

  // Signers Summary
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(99, 102, 241);
  doc.text('Signers Summary', margin, yPos);
  yPos += 10;

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);

  // Table header
  doc.setFillColor(241, 245, 249);
  doc.rect(margin, yPos - 4, pageWidth - margin * 2, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('Name', margin + 5, yPos);
  doc.text('Email', margin + 50, yPos);
  doc.text('Role', margin + 110, yPos);
  doc.text('Status', margin + 145, yPos);
  yPos += 10;

  doc.setFont('helvetica', 'normal');
  for (const signer of contract.requiredSigners) {
    checkNewPage();
    doc.text(safeStr(signer.userName).substring(0, 20), margin + 5, yPos);
    doc.text(safeStr(signer.email).substring(0, 28), margin + 50, yPos);
    doc.text(safeStr(signer.role).substring(0, 15), margin + 110, yPos);

    if (signer.signed) {
      doc.setTextColor(22, 163, 74);
      doc.text('Signed', margin + 145, yPos);
    } else {
      doc.setTextColor(202, 138, 4);
      doc.text('Pending', margin + 145, yPos);
    }
    doc.setTextColor(0, 0, 0);
    yPos += 8;
  }

  // =========================================
  // PAGE 2+: Individual Signature Details
  // =========================================

  for (let i = 0; i < contract.signatures.length; i++) {
    const sig = contract.signatures[i];
    doc.addPage();
    yPos = 20;

    // Section Header
    doc.setFillColor(99, 102, 241);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`Signature ${i + 1} of ${contract.signatures.length}`, pageWidth / 2, 22, { align: 'center' });

    yPos = 50;
    doc.setTextColor(0, 0, 0);

    // Signer Information
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(99, 102, 241);
    doc.text('Signer Information', margin, yPos);
    yPos += 12;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);

    addRow('Name', safeStr(sig.signedByName));
    addRow('Email', safeStr(sig.signedByEmail));
    addRow('Signature Type', sig.signatureType === 'drawn' ? 'Hand-drawn' : sig.signatureType === 'typed' ? 'Typed' : 'Uploaded');

    const signedDate = sig.signedAt?.toDate ? sig.signedAt.toDate() : (sig.signedAt ? new Date(sig.signedAt) : new Date());
    addRow('Signed At', format(signedDate, "MMMM d, yyyy 'at' h:mm:ss a zzz"));
    addRow('IP Address', safeStr(sig.ipAddress));

    if (sig.geolocation) {
      addRow('Location', `Lat: ${sig.geolocation.latitude.toFixed(6)}, Long: ${sig.geolocation.longitude.toFixed(6)} (±${sig.geolocation.accuracy.toFixed(0)}m)`);
    }

    yPos += 10;

    // Signature Image
    if (sig.signatureData) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(99, 102, 241);
      doc.text('Signature', margin, yPos);
      yPos += 8;

      // Signature box
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(margin, yPos, 100, 35, 2, 2, 'FD');

      try {
        if (sig.signatureType === 'typed') {
          // For typed signatures, render as text
          doc.setFontSize(20);
          doc.setFont('times', 'italic');
          doc.setTextColor(0, 0, 100);
          doc.text(sig.signatureData, margin + 5, yPos + 22);
        } else {
          // For drawn signatures, render as image
          doc.addImage(sig.signatureData, 'PNG', margin + 5, yPos + 2, 90, 30);
        }
      } catch (e) {
        doc.setFontSize(10);
        doc.setTextColor(150, 150, 150);
        doc.text('[Signature image unavailable]', margin + 5, yPos + 20);
      }

      yPos += 45;
      doc.setTextColor(0, 0, 0);
    }

    // Document Hash for this signature
    yPos += 5;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(99, 102, 241);
    doc.text('Document Verification', margin, yPos);
    yPos += 10;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Document Hash (SHA-256) at time of signing:', margin, yPos);
    yPos += 6;

    doc.setFont('courier', 'normal');
    doc.setFontSize(7);
    const hash = safeStr(sig.documentHash);
    doc.text(hash.substring(0, 64), margin, yPos);
    if (hash.length > 64) {
      yPos += 5;
      doc.text(hash.substring(64), margin, yPos);
    }
    yPos += 15;

    // Audit Trail for this signer
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(99, 102, 241);
    doc.text('Signing Ceremony Audit Trail', margin, yPos);
    yPos += 10;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);

    // Table header
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, yPos - 4, pageWidth - margin * 2, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Event', margin + 2, yPos);
    doc.text('Timestamp', margin + 55, yPos);
    doc.text('IP Address', margin + 115, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    for (const event of sig.auditTrail || []) {
      checkNewPage(15);

      const eventName = formatEventType(event.eventType);
      const eventTime = event.timestamp ? format(new Date(event.timestamp), 'MM/dd/yy HH:mm:ss') : 'N/A';

      doc.text(eventName.substring(0, 28), margin + 2, yPos);
      doc.text(eventTime, margin + 55, yPos);
      doc.text(safeStr(event.ipAddress).substring(0, 15), margin + 115, yPos);
      yPos += 6;
    }

    // User Agent (at bottom of page)
    checkNewPage(30);
    yPos += 10;
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('Browser/Device Information:', margin, yPos);
    yPos += 5;
    const uaLines = doc.splitTextToSize(safeStr(sig.userAgent), pageWidth - margin * 2);
    doc.text(uaLines, margin, yPos);
  }

  // =========================================
  // FINAL PAGE: Legal Certification
  // =========================================
  doc.addPage();
  yPos = 20;

  doc.setFillColor(99, 102, 241);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Legal Certification', pageWidth / 2, 22, { align: 'center' });

  yPos = 55;
  doc.setTextColor(0, 0, 0);

  // Certification text
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');

  const certText = [
    'This document certifies that the electronic signatures contained herein were applied',
    'in compliance with the following regulations:',
    '',
    '• The Electronic Signatures in Global and National Commerce Act (E-SIGN Act)',
    '• The Uniform Electronic Transactions Act (UETA)',
    '',
    'Each signature was captured with the following verification measures:',
    '',
    '• Unique identification of the signer through authenticated session',
    '• Timestamp recording at the moment of signature application',
    '• IP address logging for geographic and network verification',
    '• Browser/device fingerprinting for device identification',
    '• SHA-256 cryptographic hash of the document at time of signing',
    '• Complete audit trail of all actions during the signing ceremony',
    '',
    'The electronic signatures in this document are legally binding and enforceable',
    'to the same extent as handwritten signatures under applicable law.'
  ];

  for (const line of certText) {
    doc.text(line, margin, yPos);
    yPos += 7;
  }

  yPos += 15;

  // Document verification section
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, yPos, pageWidth - margin * 2, 45, 3, 3, 'F');
  yPos += 10;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Document Verification Information', margin + 5, yPos);
  yPos += 10;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Contract ID: ${contract.id}`, margin + 5, yPos);
  yPos += 6;
  doc.text(`Original Document: ${contract.documentName}`, margin + 5, yPos);
  yPos += 6;

  if (contract.documentHash) {
    doc.text(`Original Document Hash: ${contract.documentHash.substring(0, 40)}...`, margin + 5, yPos);
  }

  yPos += 20;

  // Footer
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, pageHeight - 30, pageWidth - margin, pageHeight - 30);

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text('This certificate was automatically generated and serves as a legal record of the electronic signing ceremony.', pageWidth / 2, pageHeight - 22, { align: 'center' });
  doc.text(`Generated: ${format(new Date(), "MMMM d, yyyy 'at' h:mm:ss a zzz")}`, pageWidth / 2, pageHeight - 15, { align: 'center' });

  return doc.output('blob');
}

// Generate merged compliance document (original PDF + e-sign certificate)
export async function generateMergedComplianceDocument(
  contract: ContractForPackage
): Promise<Blob> {
  try {
    // 1. Fetch the original PDF document
    const originalPdfResponse = await fetch(contract.documentUrl);
    if (!originalPdfResponse.ok) {
      throw new Error('Failed to fetch original document');
    }
    const originalPdfBytes = await originalPdfResponse.arrayBuffer();

    // 2. Generate the e-sign certificate as a PDF blob
    const certificateBlob = await generateCompleteSigningPackage(contract);
    const certificateBytes = await certificateBlob.arrayBuffer();

    // 3. Load both PDFs with pdf-lib
    const originalPdf = await PDFDocument.load(originalPdfBytes);
    const certificatePdf = await PDFDocument.load(certificateBytes);

    // 4. Create a new merged PDF document
    const mergedPdf = await PDFDocument.create();

    // 5. Copy all pages from the original document
    const originalPages = await mergedPdf.copyPages(originalPdf, originalPdf.getPageIndices());
    originalPages.forEach(page => mergedPdf.addPage(page));

    // 6. Copy all pages from the certificate document
    const certificatePages = await mergedPdf.copyPages(certificatePdf, certificatePdf.getPageIndices());
    certificatePages.forEach(page => mergedPdf.addPage(page));

    // 7. Save and return as blob
    const mergedPdfBytes = await mergedPdf.save();
    return new Blob([mergedPdfBytes], { type: 'application/pdf' });
  } catch (error) {
    console.error('Error generating merged compliance document:', error);
    // Fall back to certificate-only if merge fails
    return generateCompleteSigningPackage(contract);
  }
}

// Interface extension for merged document generation
interface ContractForMergedPackage extends ContractForPackage {
  documentUrl: string;
}

// Get geolocation (optional - requires user permission)
export async function getGeolocation(): Promise<{ latitude: number; longitude: number; accuracy: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      () => {
        // User denied or error - return null (geolocation is optional)
        resolve(null);
      },
      {
        timeout: 5000,
        maximumAge: 60000
      }
    );
  });
}
